/**
 * iter98 — Share Tracker actions.
 *
 * Two tiny buttons for admins/coordinators to hand the public `/track/{token}`
 * link to the client:
 *   • "Send on WhatsApp" — opens wa.me with a pre-filled message
 *   • "Copy Link"        — copies the tracker URL to clipboard
 *
 * Both call `GET /api/team/tracker-link?client_name=…&place=…` which reuses
 * the latest existing contract token (or creates a minimal stub if none
 * exists yet) — no manual contract-generation step required.
 *
 * Props:
 *   clientName : string   (required)
 *   place      : string
 *   whatsapp   : string   (client phone; falls back to a bare share when empty)
 *   size       : "sm" | "md"  (defaults to "md")
 */
import { useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Share2, Link as LinkIcon, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const publicOrigin = () => {
  try {
    return window.location.origin;
  } catch {
    return "";
  }
};

async function fetchTrackerLink(clientName, place) {
  const params = new URLSearchParams({ client_name: clientName, place: place || "" });
  const { data } = await axios.get(`${API}/team/tracker-link?${params.toString()}`);
  return {
    token: data.token,
    path: data.path,
    url: `${publicOrigin()}${data.path}`,
  };
}

export default function ShareTrackerActions({ clientName, place = "", whatsapp = "", size = "md" }) {
  const { user } = useAuth();
  const [waBusy, setWaBusy] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  // iter121 — Managers must NEVER see the "Copy Tracker Link" / "Share
  // Tracker" affordances. Placed AFTER the useState calls so React's
  // Rules-of-Hooks aren't violated.
  if (user?.role === "manager") return null;

  const btnCls =
    size === "sm"
      ? "inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border"
      : "inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border";

  const openWhatsApp = async () => {
    if (waBusy) return;
    setWaBusy(true);
    try {
      const link = await fetchTrackerLink(clientName, place);
      const number = String(whatsapp || "").replace(/\D/g, "");
      const message = `Hi ${clientName}, here's your live Kahani project tracker — payments, milestones and deliverables in one place:\n${link.url}`;
      const encoded = encodeURIComponent(message);
      const url = number
        ? `https://wa.me/${number}?text=${encoded}`
        : `https://wa.me/?text=${encoded}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success(number ? "Tracker sent to WhatsApp" : "Pick a contact to share the tracker");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't build tracker link");
    } finally {
      setWaBusy(false);
    }
  };

  const copyLink = async () => {
    if (copyBusy) return;
    setCopyBusy(true);
    try {
      const link = await fetchTrackerLink(clientName, place);
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success("Tracker link copied");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Couldn't copy tracker link");
    } finally {
      setCopyBusy(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-1.5" data-testid="share-tracker-actions">
      <button
        type="button"
        onClick={openWhatsApp}
        disabled={waBusy}
        className={`${btnCls} border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50 transition`}
        title="Send the client their live tracker on WhatsApp"
        data-testid="share-tracker-whatsapp"
      >
        {waBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" strokeWidth={2.2} />}
        Share Tracker
      </button>
      <button
        type="button"
        onClick={copyLink}
        disabled={copyBusy}
        className={`${btnCls} border-stone-200 bg-white text-stone-700 hover:bg-stone-100 disabled:opacity-50 transition`}
        title="Copy the /track/… URL to clipboard"
        data-testid="share-tracker-copy"
      >
        {copyBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : copied ? <Check className="w-3 h-3 text-emerald-600" strokeWidth={2.4} /> : <LinkIcon className="w-3 h-3" strokeWidth={2.2} />}
        {copied ? "Copied" : "Copy link"}
      </button>
    </div>
  );
}
